import classNames from 'classnames';
import React, { useState } from 'react';
import IconButton from 'components/styleguide/icon_button/IconButton';

const styles = require('./Slider.module.scss');

export type SliderContext = 'talent' | 'company' | 'neutral' | 'semantic';

export interface SliderProps {
  'aria-label'?: string;
  'aria-valuetext'?: string;
  className?: string;
  context?: SliderContext;
  defaultValue?: number;
  disabled?: boolean;
  id?: string;
  max?: number;
  min?: number;
  name?: string;
  onChange?: (value: number) => void;
  step?: number;
  stepDownLabel?: string;
  stepUpLabel?: string;
  steppers?: boolean;
  value?: number;
}

const clamp = (value: number, min: number, max: number) => Math.min(max, Math.max(min, value));
const roundToStep = (value: number, step: number) =>
  Number(value.toFixed((String(step).split('.')[1] || '').length));

const Slider: React.FC<SliderProps> = ({
  'aria-label': ariaLabel,
  'aria-valuetext': ariaValueText,
  className,
  context = 'talent',
  defaultValue = 0,
  disabled = false,
  id,
  max = 100,
  min = 0,
  name,
  onChange,
  step = 1,
  stepDownLabel = 'Decrease',
  stepUpLabel = 'Increase',
  steppers = false,
  value,
}) => {
  const [internal, setInternal] = useState(defaultValue);
  const current = value ?? internal;
  const fillPercent = max === min ? 0 : ((current - min) / (max - min)) * 100;

  const commit = (next: number) => {
    const clamped = clamp(roundToStep(next, step), min, max);
    if (value === undefined) setInternal(clamped);
    onChange?.(clamped);
  };

  const input = (
    <input
      type="range"
      id={id}
      name={name}
      className={classNames(
        styles['ui-slider'],
        styles[`ui-slider--context-${context}`],
        !steppers && className,
      )}
      style={{ '--ui-slider-fill': `${fillPercent}%` } as React.CSSProperties}
      min={min}
      max={max}
      step={step}
      value={current}
      disabled={disabled}
      aria-label={ariaLabel}
      aria-valuetext={ariaValueText}
      onChange={(event) => commit(Number(event.target.value))}
    />
  );

  if (!steppers) return input;

  return (
    <div className={classNames(styles['ui-slider-group'], className)}>
      <IconButton
        type="secondary"
        context={context}
        size="M"
        iconName="minus"
        aria-label={stepDownLabel}
        disabled={disabled || current <= min}
        onClick={() => commit(current - step)}
      />
      {input}
      <IconButton
        type="secondary"
        context={context}
        size="M"
        iconName="plus"
        aria-label={stepUpLabel}
        disabled={disabled || current >= max}
        onClick={() => commit(current + step)}
      />
    </div>
  );
};

export default Slider;
